import React, { useCallback, useId, useMemo, useState } from 'react';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useDispatch } from 'react-redux';

import { Badge } from 'src/component/ds/ui/badge';
import { Button } from 'src/component/ds/ui/button';
import { Card, CardTitle } from 'src/component/ds/ui/card';
import {
  ChartSkeleton,
  HeadlineSkeleton
} from 'src/screen/v2/Protected/DashboardScreen/PotentialEnergySavings/PotentialEnergySavingsSkeleton';
import EnergyChart, {
  type EnergySeriesPoint
} from 'src/screen/v2/Protected/DashboardScreen/PotentialEnergySavings/EnergyChart';
import {
  DEFAULT_ENERGY_PERIOD,
  ENERGY_PERIODS,
  PERIOD_LABEL_KEY,
  type EnergyPeriod
} from 'src/screen/v2/Protected/DashboardScreen/PotentialEnergySavings/period';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'src/component/ds/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/component/ds/ui/tooltip';
import {
  formatCurrency,
  formatEnergy,
  formatRate,
  scaleEnergyPair
} from 'src/screen/v2/Protected/DashboardScreen/PotentialEnergySavings/format';
import { cn } from 'src/component/ds/utils';
import { pushHistory } from 'src/module/navigation/action';
import { setSelectedAttribute } from 'src/module/filter/action';
import { useTranslation } from 'src/i18n';


const DEVICE_LIST_PATH = '/devices/list';

export type { EnergySeriesPoint };

export interface PotentialEnergySavingsProps {
  savings?: number;
  currency?: string;
  /** $/kWh the headline was costed at. Disclosed only when it is the default. */
  ratePerKwh?: number;
  /** True when no facility rate is set and the API's default was used. */
  rateIsDefault?: boolean;
  wastedKwh?: number;
  totalKwh?: number;
  series?: EnergySeriesPoint[];
  period?: EnergyPeriod;
  onPeriodChange?: (period: EnergyPeriod) => void;
  loading?: boolean;
  error?: boolean;
  className?: string;
}

/**
 * The dashboard's Potential Energy Savings card: what circuits heating past their
 * control setpoint are costing this facility, over a selectable period.
 *
 * @param {PotentialEnergySavingsProps} props see PotentialEnergySavingsProps
 * @returns {JSX.Element} the card
 */
export default function PotentialEnergySavings ({
  savings = 0,
  currency = 'USD',
  ratePerKwh,
  rateIsDefault = true,
  wastedKwh = 0,
  totalKwh = 0,
  series = [],
  period = DEFAULT_ENERGY_PERIOD,
  onPeriodChange,
  loading = false,
  error = false,
  className
}: PotentialEnergySavingsProps) {
  const { t, language } = useTranslation();
  const dispatch = useDispatch();
  const titleId = useId();

  const [showAmbient, setShowAmbient] = useState(true);
  const [showEnergy, setShowEnergy] = useState(true);

  const openForcedOn = useCallback(() => {
    dispatch(setSelectedAttribute('forcedOn'));
    dispatch(pushHistory(DEVICE_LIST_PATH));
  }, [dispatch]);

  const handlePeriodChange = useCallback((value: string) => {
    onPeriodChange?.(value as EnergyPeriod);
  }, [onPeriodChange]);

  const scaled = useMemo(() => scaleEnergyPair(wastedKwh, totalKwh), [wastedKwh, totalKwh]);

  const hasData = series.length > 0 || totalKwh > 0;
  const status = error ? 'error' : !hasData ? 'empty' : null;

  const rateHint = (
    <div className='ds-flex ds-flex-col ds-gap-3'>
      <p>{t('dashboard.optimizeUsageHintIntro')}</p>
      <ul className='ds-flex ds-list-disc ds-flex-col ds-gap-1 ds-pl-4'>
        <li>{t('dashboard.optimizeUsageHintForces')}</li>
        <li>{t('dashboard.optimizeUsageHintProgramming')}</li>
        <li>{t('dashboard.optimizeUsageHintDesignedValue')}</li>
      </ul>
      <p>{t('dashboard.optimizeUsageHintSupport')}</p>

      {rateIsDefault && ratePerKwh != null && (
        <p className='ds-opacity-80' data-testid='potential-energy-savings-rate-hint'>
          {t('dashboard.optimizeUsageHintDefaultRate', {
            rate: formatRate(ratePerKwh, currency, language),
          })}
        </p>
      )}
    </div>
  );

  const isOptimized = !status && wastedKwh <= 0;

  const optimizeButton = (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='outline'
            size='xs'
            className={cn('ds-w-fit ds-rounded-full', isOptimized && 'ds-cursor-default')}
            onClick={isOptimized ? undefined : openForcedOn}
            data-testid='potential-energy-savings-optimize'
            data-optimized={isOptimized || undefined}
          >
            {isOptimized
              ? <CheckOutlined fontSize='inherit' aria-hidden='true' />
              : <InfoOutlined fontSize='inherit' aria-hidden='true' />}
            <span>{t(isOptimized ? 'dashboard.fullyOptimized' : 'dashboard.optimizeUsage')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent className='ds-max-w-[420px]'>
          <div className='ds-p-2'>{isOptimized ? t('dashboard.fullyOptimizedHint') : rateHint}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Card className={cn('ds-shadow-sm', className)} data-testid='potential-energy-savings'>
      <div className='ds-p-8'>
        <div className='ds-grid ds-grid-cols-1 ds-gap-8 ds-narrow:ds-grid-cols-[minmax(166px,auto)_minmax(313px,1fr)]'>

          <div className='ds-flex ds-min-w-0 ds-flex-col ds-justify-between ds-gap-4'>
            <div className='ds-flex ds-min-h-[36px] ds-items-center ds-justify-between ds-gap-4'>
              <CardTitle id={titleId} className='ds-truncate ds-text-sm ds-font-normal ds-text-muted-foreground'>
                {t('dashboard.potentialEnergySavings')}
              </CardTitle>

              <div className='ds-shrink-0 ds-narrow:ds-hidden'>{optimizeButton}</div>
            </div>

            <div className='ds-flex ds-flex-1 ds-items-start ds-justify-between ds-gap-4'>
              {loading ? <HeadlineSkeleton /> : (
                <div className='ds-flex ds-min-w-0 ds-flex-col ds-gap-3'>
                  <p
                    className='ds-font-heading ds-text-chemelex-h1 ds-font-medium ds-tracking-[-1.5px] ds-text-foreground'
                    data-testid='potential-energy-savings-headline'
                    aria-describedby={titleId}
                  >
                    {status ? '--' : formatCurrency(savings, currency, language)}
                  </p>

                  {status ? (
                    <p
                      className='ds-text-xs ds-text-muted-foreground'
                      data-testid={`potential-energy-savings-${status}`}
                    >
                      {t(status === 'error' ? 'dashboard.energySavingsError' : 'dashboard.energySavingsNoData')}
                    </p>
                  ) : (
                    <Badge
                      variant='secondary'
                      className='ds-max-w-full'
                      data-testid='potential-energy-savings-chip'
                    >
                      <span className='ds-truncate'>
                        <span className='ds-font-semibold'>{formatEnergy(scaled.a, language)}</span>
                        {` / ${formatEnergy(scaled.b, language)} ${scaled.unit} ${t('dashboard.wasted')}`}
                      </span>
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className='ds-hidden ds-narrow:ds-block'>{optimizeButton}</div>
          </div>

          <div className='ds-flex ds-min-w-0 ds-flex-col ds-gap-[18px]'>
            <div className='ds-flex ds-min-h-[36px] ds-flex-wrap ds-items-center ds-justify-end ds-gap-x-3 ds-gap-y-2'>
              <div className='ds-flex ds-min-w-0 ds-flex-wrap ds-items-center ds-gap-x-4 ds-gap-y-2'>
                <LegendToggle
                  color='var(--ds-chart-ambient)'
                  label={t('dashboard.ambientTemp')}
                  active={showAmbient}
                  onToggle={() => setShowAmbient((v) => !v)}
                  testId='potential-energy-savings-legend-ambient'
                />
                <LegendToggle
                  color='var(--ds-chart-energy)'
                  label={t('dashboard.totalKwh')}
                  active={showEnergy}
                  onToggle={() => setShowEnergy((v) => !v)}
                  testId='potential-energy-savings-legend-energy'
                />
              </div>

              <Select value={period} onValueChange={handlePeriodChange}>
                <SelectTrigger
                  size='xs'
                  className='ds-w-[108px] ds-shrink-0'
                  aria-label={t('dashboard.energyPeriodLabel')}
                  data-testid='potential-energy-savings-period'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENERGY_PERIODS.map((p) => (
                    <SelectItem key={p} value={p}>{t(PERIOD_LABEL_KEY[p])}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? <ChartSkeleton /> : hasData ? (
              <EnergyChart
                series={series}
                showEnergy={showEnergy}
                showAmbient={showAmbient}
                className='ds-h-[153px]'
                role='img'
                aria-label={t('dashboard.energyChartLabel')}
                data-testid='potential-energy-savings-chart'
              />
            ) : (
              <div
                className='ds-flex ds-h-[153px] ds-items-center ds-justify-center ds-text-xs ds-text-muted-foreground'
                data-testid='potential-energy-savings-chart-empty'
              >
                {t('dashboard.energySavingsNoData')}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

interface LegendToggleProps {
  color: string;
  label: string;
  active: boolean;
  onToggle: () => void;
  testId: string;
}

function LegendToggle ({ color, label, active, onToggle, testId }: LegendToggleProps) {
  return (
    <button
      type='button'
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        'ds-flex ds-min-w-0 ds-items-center ds-gap-1 ds-text-xs ds-text-card-foreground',
        'focus-visible:ds-outline-none focus-visible:ds-shadow-chemelex-focus ds-rounded',
        !active && 'ds-opacity-40'
      )}
      data-testid={testId}
    >
      <span
        className='ds-h-2 ds-w-2 ds-shrink-0 ds-rounded'
        style={{ backgroundColor: color }}
      />
      <span className='ds-truncate'>{label}</span>
    </button>
  );
}
